import { useEffect, useState } from "react";
import { Card, Nav, Tab } from "react-bootstrap";
import { organizationStore } from "../../store/OrganizationStore";
import { appStore } from "@store/appStore";
import { useSnapshot } from "valtio";
import { Link, useParams, useLocation, useSearchParams } from "react-router-dom";
import { Paths } from "../../paths";
import { OrganizationService } from "@services/organizations/OrganizationService";
import useOrganization from "./hooks/useOrganization";
import useOrganizationBrand from "./hooks/useOrganizationBrand";
import OrganizationGeneralTab from "./components/OrganizationGeneralTab";
import OrganizationBrandTab from "./components/OrganizationBrandTab";
import OrganizationNetworkAccountsTab from "./components/OrganizationNetworkAccountsTab";
import OrganizationContractsTab from "./contracts/components/OrganizationContractsTab";
import OrganizationSegmentsTab from "./segments/components/OrganizationSegmentsTab";
import OrganizationApprovedUrlsTab from "./approvedUrls/components/OrganizationApprovedUrlsTab";
import OrganizationUsersTab from "./users/components/OrganizationUsersTab";
import OrganizationSftpTab from "./sftp/components/OrganizationSftpTab";
import Message from "../../components/common/Message";
import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";
import ReturnToJob from "@components/ReturnToJob/ReturnToJob";
import { DeliverySetupTab } from "./components/DeliverySetupTab";

const VALID_TABS = ["general", "brand", "approved-urls", "contracts", "segments", "users", "network-accounts", "delivery", "sftp"];

export default function OrganizationDetail() {
  useSnapshot(organizationStore);
  const { organizationForm, setOrganizationForm, updateOrg, getOrganization } = useOrganization();
  const { brandForm, setBrandForm, savedBrand, update, saveSection, colorsForm, setColorsForm, savedColors, loadColors, saveColors, typographyForm, setTypographyForm, loadTypographyStyles, saveTypographyStyles, adTokenValuesForm, setAdTokenValuesForm, availableAdTokens, loadAdTokenValues, saveAdTokenValues, assetsForm, setAssetsForm, saveAssets, getOrganizationBrand, isBrandLoading, uploadAndLinkFont, unlinkFont, setDefaultLogo, addAsset, replaceAsset, setAssetTag, isFontUploading } =
    useOrganizationBrand();

  useDocumentTitle(brandForm.name || organizationForm.name || "Organization");
  const { organizationId = "" } = useParams();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const backQuery = (location.state as { q?: string })?.q;

  const tabFromUrl = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState(
    tabFromUrl && VALID_TABS.includes(tabFromUrl) ? tabFromUrl : "general"
  );

  const hasAccess = (feature: string) =>
    appStore.loggedInUser?.permissions?.hasAccess?.(feature) ?? false;

  useEffect(() => {
    const tabFeatureMap: Record<string, string> = {
      contracts: "Contracts",
      users: "Users",
      "network-accounts": "Network Setup",
      delivery: "Delivery Setup",
      sftp: "Ingest Files",
    };
    const requiredFeature = tabFeatureMap[activeTab];
    if (requiredFeature && !hasAccess(requiredFeature)) {
      setActiveTab("general");
    }
  }, [activeTab]);

  useEffect(() => {
    if (organizationId) {
      getOrganization(organizationId);
      getOrganizationBrand(organizationId);
    }
  }, [organizationId]);

  // The org list is needed to resolve service-provider names in the Contracts tab.
  // It's normally populated by the Organizations index page; load it here too
  // so direct loads / refreshes don't show "Org #4" fallbacks.
  useEffect(() => {
    if (organizationStore.organizationList.length > 0) return;
    const userOrgId = appStore.loggedInUser?.organization?.id;
    if (userOrgId == null) return;
    new OrganizationService().getOrganizations(userOrgId).then((orgs) => {
      if (orgs) organizationStore.setOrganizations(orgs);
    });
  }, []);

  return (
    <div className="px-4 py-3">
      <ReturnToJob />
      <Message marginTop="16px" />
      <div className="d-flex align-items-center mb-4">
        <Link className="btn btn-light btn-sm me-3" to={`../${Paths.ORGANIZATIONS}${backQuery ? `?q=${encodeURIComponent(backQuery)}` : ""}`}>
          <i className="bi bi-arrow-left me-2"></i>
          Back
        </Link>
        <div>
          <h3 className="mb-1 fw-bold">{brandForm.name || organizationForm.name || "Organization"}</h3>
          <p className="text-muted mb-0">Manage organization settings and branding</p>
        </div>
      </div>

      <Card className="border-0 shadow-sm">
        <Tab.Container
          id="org-tabs"
          activeKey={activeTab}
          onSelect={(k) => {
            const tab = k || "general";
            setActiveTab(tab);
            const params = new URLSearchParams(searchParams);
            if (tab === "general") params.delete("tab");
            else params.set("tab", tab);
            setSearchParams(params, { replace: true });
          }}
        >
          <Card.Header className="bg-white border-bottom">
            <Nav variant="tabs" className="nav-tabs-custom">
              <Nav.Item>
                <Nav.Link eventKey="general" className="d-flex align-items-center gap-2">
                  <i className="bi bi-info-circle"></i>
                  General
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="brand" className="d-flex align-items-center gap-2">
                  <i className="bi bi-palette"></i>
                  Branding
                </Nav.Link>
              </Nav.Item>
              {hasAccess("Contracts") && (
                <Nav.Item>
                  <Nav.Link eventKey="contracts" className="d-flex align-items-center gap-2">
                    <i className="bi bi-file-earmark-text"></i>
                    Contracts
                  </Nav.Link>
                </Nav.Item>
              )}
              <Nav.Item>
                <Nav.Link eventKey="segments" className="d-flex align-items-center gap-2">
                  <i className="bi bi-diagram-3"></i>
                  Segments
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="approved-urls" className="d-flex align-items-center gap-2">
                  <i className="bi bi-link-45deg"></i>
                  Approved URLs
                </Nav.Link>
              </Nav.Item>
              {hasAccess("Users") && (
                <Nav.Item>
                  <Nav.Link eventKey="users" className="d-flex align-items-center gap-2">
                    <i className="bi bi-people"></i>
                    Users
                  </Nav.Link>
                </Nav.Item>
              )}
              {hasAccess("Network Setup") && (
                <Nav.Item>
                  <Nav.Link eventKey="network-accounts" className="d-flex align-items-center gap-2">
                    <i className="bi bi-link-45deg"></i>
                    Network Setup
                  </Nav.Link>
                </Nav.Item>
              )}
              {hasAccess("Delivery Setup") && (
                <Nav.Item>
                  <Nav.Link eventKey="delivery" className="d-flex align-items-center gap-2">
                    <i className="bi bi-envelope"></i>
                    Delivery Setup
                  </Nav.Link>
                </Nav.Item>
              )}
              {hasAccess("Ingest Files") && (
                <Nav.Item>
                  <Nav.Link eventKey="sftp" className="d-flex align-items-center gap-2">
                    <i className="bi bi-hdd-network"></i>
                    SFTP Settings
                  </Nav.Link>
                </Nav.Item>
              )}
            </Nav>
          </Card.Header>

          <Card.Body className="p-4">
            <Tab.Content>
              <Tab.Pane eventKey="general">
                <OrganizationGeneralTab
                  organizationForm={organizationForm}
                  setOrganizationForm={setOrganizationForm}
                  updateOrg={updateOrg}
                />
              </Tab.Pane>

              <Tab.Pane eventKey="brand">
                <OrganizationBrandTab
                  brandForm={brandForm}
                  setBrandForm={setBrandForm}
                  update={() => update(organizationId)}
                  savedBrand={savedBrand}
                  onSaveSection={(fields) => saveSection(organizationId, fields)}
                  organizationName={organizationForm.name}
                  colors={colorsForm}
                  savedColors={savedColors}
                  setColors={setColorsForm}
                  onLoadColors={() => loadColors(organizationId)}
                  onSaveColors={() => saveColors(organizationId)}
                  typographyStyles={typographyForm}
                  setTypographyStyles={setTypographyForm}
                  onLoadTypographyStyles={() => loadTypographyStyles(organizationId)}
                  onSaveTypographyStyles={() => saveTypographyStyles(organizationId)}
                  adTokenValues={adTokenValuesForm}
                  setAdTokenValues={setAdTokenValuesForm}
                  availableAdTokens={availableAdTokens}
                  onLoadAdTokenValues={() => loadAdTokenValues(organizationId)}
                  onSaveAdTokenValues={() => saveAdTokenValues(organizationId)}
                  assets={assetsForm}
                  setAssets={setAssetsForm}
                  onSaveAssets={(kind) => saveAssets(organizationId, kind)}
                  isLoading={isBrandLoading}
                  onUploadFont={(file) => uploadAndLinkFont(organizationId, file)}
                  onUnlinkFont={(fontId) => unlinkFont(organizationId, fontId)}
                  onSetDefaultLogo={(assetId) => setDefaultLogo(organizationId, assetId)}
                  onAddAsset={(file, makeDefault) => addAsset(organizationId, file, makeDefault)}
                  onReplaceAsset={(assetId, file) => replaceAsset(organizationId, assetId, file)}
                  onSetAssetTag={(assetId, tag) => setAssetTag(organizationId, assetId, tag)}
                  isFontUploading={isFontUploading}
                />
              </Tab.Pane>

              {hasAccess("Contracts") && (
                <Tab.Pane eventKey="contracts">
                  <OrganizationContractsTab
                    organizationId={Number(organizationId)}
                    organizations={organizationStore.organizationList}
                  />
                </Tab.Pane>
              )}

              <Tab.Pane eventKey="segments">
                <OrganizationSegmentsTab organizationId={Number(organizationId)} />
              </Tab.Pane>

              <Tab.Pane eventKey="approved-urls">
                <OrganizationApprovedUrlsTab organizationId={Number(organizationId)} />
              </Tab.Pane>

              {hasAccess("Users") && (
                <Tab.Pane eventKey="users">
                  <OrganizationUsersTab organizationId={Number(organizationId)} />
                </Tab.Pane>
              )}

              {hasAccess("Network Setup") && (
                <Tab.Pane eventKey="network-accounts">
                  <OrganizationNetworkAccountsTab organizationId={Number(organizationId)} />
                </Tab.Pane>
              )}

              {hasAccess("Delivery Setup") && (
                <Tab.Pane eventKey="delivery">
                  <DeliverySetupTab organizationId={Number(organizationId)} />
                </Tab.Pane>
              )}

              {hasAccess("Ingest Files") && (
                <Tab.Pane eventKey="sftp">
                  <OrganizationSftpTab organizationId={Number(organizationId)} />
                </Tab.Pane>
              )}
            </Tab.Content>
          </Card.Body>
        </Tab.Container>
      </Card>

      <style>{`
        .nav-tabs-custom .nav-link {
          border: none;
          border-bottom: 2px solid transparent;
          color: #6c757d;
          padding: 0.75rem 1.5rem;
          font-weight: 500;
          border-radius: 0;
        }
        .nav-tabs-custom .nav-link:hover {
          border-bottom-color: #dee2e6;
          color: #495057;
        }
        .nav-tabs-custom .nav-link.active {
          color: #0d6efd;
          border-bottom-color: #0d6efd;
          background: none;
        }
      `}</style>
    </div>
  );
}